export type AccuracyGateStatus = 'passed' | 'failed' | 'partial' | 'skipped';

export type FindingStatus = 'proposed' | 'approved' | 'adjusted' | 'rejected' | 'kept_flagged';

export type FindingFix =
  | 'insert_step'
  | 'delete_step'
  | 'reassign_actor'
  | 'mark_decision'
  | 'set_decision_paths'
  | 'rewire_edge'
  | 'classify_annotation'
  | 'restore_end'
  | 'strip_enrichment'
  | 'add_stakeholder'
  | 'flag_for_sme';

export const NEW_END_SENTINEL = '__new_end__';

export interface DecisionPathsProposal {
  yes?: string;
  no?: string;
  yes_label?: string;
  no_label?: string;
  yes_condition?: string;
  no_condition?: string;
}

export interface AccuracyGateFinding {
  id: string;
  check_id: string;
  severity: 'meaningful' | 'nit';
  status: FindingStatus;
  fix: string;
  source?: { lane?: string | null; text?: string; quote?: string; track_id?: string };
  target?: {
    type?: string;
    step_id?: string;
    insert_after_step_id?: string | null;
    edge?: { from?: string; to?: string };
  };
  proposal?: {
    name?: string;
    description?: string;
    actor?: string | null;
    step_type?: 'process' | 'decision' | 'start' | 'end' | 'annotation';
    insert_after_step_id?: string | null;
    decision_paths?: DecisionPathsProposal;
    next_step_id?: string | null;
    classification?: string;
    [key: string]: unknown;
  };
}

export interface AccuracyGate {
  status: AccuracyGateStatus;
  generated_at?: string;
  checks?: Array<{ id: string; status: string; source_count?: number | null; jarvis_count?: number | null }>;
  findings?: AccuracyGateFinding[];
  score?: { overall?: number; formula?: string; headline?: string };
}

/**
 * Verifier step 1 is done when the accuracy gate has stored a run.
 * Failed packs with findings still count as done (Edit needs the proposals).
 * Legacy claim-pipeline metadata is NOT verifier-done.
 */
export function isAccuracyGateDone(gate: AccuracyGate | null | undefined): boolean {
  return !!(gate && typeof gate.generated_at === 'string' && gate.generated_at.length > 0);
}

export function accuracyGateHeadline(gate: AccuracyGate | null | undefined): string {
  return gate?.score?.headline || 'Accuracy gate ran';
}

/** Source diagram had at least one YES/NO diamond. Linear (Calm) maps are 0. */
export function sourceHasDiamonds(gate: AccuracyGate | null | undefined): boolean {
  const check = gate?.checks?.find((c) => c.id === 'decisions');
  return (check?.source_count || 0) > 0;
}

export function mergeFindingStatuses(
  findings: AccuracyGateFinding[] | undefined,
  session: Record<string, FindingStatus> | undefined,
): AccuracyGateFinding[] {
  return (findings || []).map((f) => ({
    ...f,
    status: (session && session[f.id]) || f.status || 'proposed',
  }));
}

/** Overlay rows (new dashed steps). Not written to map_data until Approve/Adjust. */
export function isInsertOverlayFinding(finding: AccuracyGateFinding): boolean {
  if (finding.fix === 'insert_step' || finding.fix === 'restore_end') return true;
  return finding.target?.type === 'new_step' && finding.fix !== 'add_stakeholder';
}

export function isExistingRowFinding(finding: AccuracyGateFinding): boolean {
  if (isInsertOverlayFinding(finding)) return false;
  if (finding.fix === 'add_stakeholder') return false;
  return !!(finding.target?.step_id);
}

export function isOpenProposal(finding: AccuracyGateFinding): boolean {
  return finding.status === 'proposed' || finding.status === 'kept_flagged';
}

export function overlayFindings(findings: AccuracyGateFinding[]): AccuracyGateFinding[] {
  return findings.filter((f) => f.status === 'proposed' && isInsertOverlayFinding(f));
}

export function existingRowFindingsForStep(
  findings: AccuracyGateFinding[],
  stepId: string,
): AccuracyGateFinding[] {
  return findings.filter(
    (f) => isOpenProposal(f) && isExistingRowFinding(f) && String(f.target?.step_id) === String(stepId),
  );
}

export function stakeholderFindings(findings: AccuracyGateFinding[]): AccuracyGateFinding[] {
  return findings.filter((f) => f.status === 'proposed' && f.fix === 'add_stakeholder');
}

export function findingReason(finding: AccuracyGateFinding): string {
  const lane = finding.source?.lane;
  const text = finding.source?.text || finding.proposal?.name || '';
  const track = finding.source?.track_id ? ` in ${finding.source.track_id} track` : '';
  switch (finding.check_id) {
    case 'missing_source_step':
      return `Missing source step${track}${text ? `: “${text}”` : ''}`;
    case 'interstitial_miss':
      return `Skipped source step between neighbors${text ? `: “${text}”` : ''}`;
    case 'wrong_actor':
      return lane ? `Wrong actor — source lane is ${lane}` : 'Wrong actor';
    case 'empty_lane_actor':
      return `Actor is an empty source lane${lane ? ` (${lane})` : ''}`;
    case 'extra_jarvis_step':
      return 'Extra Jarvis step not in source';
    case 'invented_routing_label':
      return 'Invented routing label — not an explicit source branch';
    case 'should_be_decision':
      return 'Should be a decision (gateway) with YES/NO paths';
    case 'missing_yes_no':
      return 'Decision is missing a distinct YES or NO path';
    case 'annotation_inlined':
      return 'Support/annotation was placed in-line as a sequential step';
    case 'track_edge':
      return 'Sequence edge crosses source-disconnected tracks';
    case 'dotted_extra_edge':
      return 'Unexplained dotted extra edge';
    case 'missing_start':
      return `Missing START marker${track}`;
    case 'missing_end':
      return `Missing END marker${track}`;
    case 'enrichment':
      return 'Step wording is richer than the matched source line';
    case 'named_participant':
      return `Named participant not in stakeholders${text ? `: ${text}` : ''}`;
    case 'ambiguity':
    case 'sop_step_lock':
      return finding.source?.text || 'Needs SME review — do not invent a path';
    default:
      return text ? `Proposed correction: ${text}` : 'Proposed correction';
  }
}

export function stepTypeOf(step: { action_type?: string; step_type?: string; bpmn_type?: string; event_type?: string; is_decision?: boolean; classification?: string } | null | undefined): string {
  if (!step) return 'process';
  const action = String(step.action_type || step.step_type || '').toLowerCase();
  const bpmn = String(step.bpmn_type || '').toLowerCase();
  const event = String(step.event_type || '').toLowerCase();
  if (action === 'start' || event === 'startevent' || bpmn === 'startevent') return 'start';
  if (action === 'end' || event === 'endevent' || bpmn === 'endevent') return 'end';
  if (action === 'decision' || action === 'gateway' || bpmn === 'gateway' || step.is_decision === true) {
    return 'decision';
  }
  if (action === 'annotation' || step.classification === 'annotation') return 'annotation';
  return 'process';
}

export function isBoundaryStep(step: { action_type?: string; step_type?: string; bpmn_type?: string; event_type?: string } | null | undefined): boolean {
  const t = stepTypeOf(step);
  return t === 'start' || t === 'end';
}

/** Both YES and NO must be set before Approve of a should-be-decision finding when the source had diamonds. */
export function decisionPathsComplete(paths: { yes?: string; no?: string } | null | undefined): boolean {
  return !!(paths?.yes && String(paths.yes) && paths?.no && String(paths.no) && String(paths.yes) !== String(paths.no));
}

export function requiresBothDecisionPaths(finding: AccuracyGateFinding, diamondsInSource: boolean): boolean {
  if (!diamondsInSource) return false;
  return finding.fix === 'mark_decision' || finding.fix === 'set_decision_paths';
}

export function stakeholderNamesFromMap(mapData: any): string[] {
  const raw = mapData?.elements?.stakeholders || mapData?.stakeholders;
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((s) => (typeof s === 'string' ? s : s?.name || s?.role || s?.title || '')).filter(Boolean);
  }
  if (typeof raw === 'object') return Object.keys(raw);
  return [];
}
